import { FC, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetCountryListForDropdowns } from "../../../../../api/services/client/clientApis";
import { useGetDeliveryOfficeListQuery } from "../../../../../api/services/mdmOffice/mdmOfficeApis";
import {
  useGetProgramClass,
  useGetSolutionOffering,
} from "../../../../../api/services/programTemplate/programTemplateApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  AutoCompleteOption,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
  OptionsDataTypeWithNode,
  SearchSaveFiltersSubHeader,
  STATUS_BADGE_TYPE,
} from "../../../../../components";
import { ClientAutoComplete } from "../../../../../components/clientAutoComplete/ClientAutoComplete";
import {
  LANGUAGE_CODE,
  PAGE_IDS,
  ROLE_IDS,
} from "../../../../../helpers/constants";
import {
  getCountryCodeWithoutCountryPrefix,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  mapDropdownDataWithName,
  mapSelectOptionToFirstIndex,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { GenericAccountsSearchFormKey } from "../../modelsAndSchemas/Models";
import { genericAccountsSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { GenericAccountsSearchFormWrapper } from "./GenericAccountsSearchForm.style";
interface GenericAccountsSearchProps {
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  handleSaveFilter: () => void;
  handleSavedFilter: () => void;
  disableActionBtn: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement>;
  deliveryOfficeRef: React.Ref<{ resetValues: () => void }>;
  clientRef: React.Ref<{ resetValues: () => void }>;
}

const pageId = PAGE_IDS.GENERIC_ACCOUNTS_SEARCH;
const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

export const GenericAccountsSearchForm: FC<GenericAccountsSearchProps> = ({
  handleClearAllSearch,
  handleSearchClick,
  handleSaveFilter,
  handleSavedFilter,
  disableActionBtn,
  anchorRef,
  deliveryOfficeRef,
  clientRef,
}) => {
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const dispatch = useDispatch();

  const { data: countryListData, isError } = useGetCountryListForDropdowns();

  const { setValue, control, getValues } = useFormContext();

  const formValues = useWatch({ control });

  const clientValue = useWatch({
    name: GenericAccountsSearchFormKey.clientId,
    control,
    defaultValue: [],
  });

  useEffect(() => {
    if (isEmpty(clientValue)) {
      setSelectedClientObject(undefined);
      setValue(GenericAccountsSearchFormKey.clientName, "");
    }
  }, [clientValue]);

  const [selectedClientObject, setSelectedClientObject] = useState<
    | {
        id: string;
        label: string;
      }
    | undefined
  >(undefined);

  // While disabling the save btn - empty form - Fields to ignore in the check
  const ignoredFields = [
    GenericAccountsSearchFormKey.genericAccountNameFilter,
  ] as string[];

  // While disabling the save btn - empty form - Check if any of the fields have values in them
  const isValidForm = Object.entries(formValues).some(
    ([key, value]) =>
      !ignoredFields.includes(key) &&
      (typeof value === "string" ? value.trim() !== "" : value.length > 0),
  );

  const {
    mutateAsync: getUsersDetailsByRole,
    data: usersList,
    isPending: isUsersListPending,
    isError: isUsersListError,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    getUsersDetailsByRole({
      roleIds: [
        ROLE_IDS.sales,
        ROLE_IDS.admin,
        ROLE_IDS.corporateAdmin,
        ROLE_IDS.outreachConsultantLead,
        ROLE_IDS.projectManager,
        ROLE_IDS.projectCoOrdinator,
      ],
    });
    setValue(
      GenericAccountsSearchFormKey.genericAccountStatus,
      STATUS_BADGE_TYPE.active,
    );
  }, []);

  const { data: solutionOfferings, isError: isSolutionOfferingError } =
    useGetSolutionOffering(LANGUAGE_CODE.EN_US, true);

  const {
    data: programClasses,
    isLoading: isClassPending,
    refetchWithParams: refetchProgramClass,
    isError: isProgramClassError,
  } = useGetProgramClass();

  const {
    refetchWithParams: refetchDeliveryOffice,
    data: deliveryOfficeListData,
    isLoading: isDeliveryOfficeListPending,
  } = useGetDeliveryOfficeListQuery();

  const getLabel = (key: keyof typeof genericAccountsSearchSiteCoreKeys) => {
    return fetchLabel(
      genericAccountsSearchSiteCoreKeys.searchGenericAccountSection,
      genericAccountsSearchSiteCoreKeys[key],
    );
  };

  useEffect(() => {
    if (
      isError ||
      isProgramClassError ||
      isUsersListError ||
      isSolutionOfferingError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError, isProgramClassError, isUsersListError, isSolutionOfferingError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isClassPending || isUsersListPending || isDeliveryOfficeListPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isClassPending, isUsersListPending, isDeliveryOfficeListPending]);

  const [selectedCountryId, setSelectedCountryId] = useState<string>("");

  const handleCountryChange = (countryId: string) => {
    setSelectedCountryId(countryId);
    refetchDeliveryOffice(
      countryId
        ? getCountryCodeWithoutCountryPrefix(countryId).toUpperCase()
        : "",
    );
  };

  const handleSolutionOfferingChange = async (id: string) => {
    setValue(GenericAccountsSearchFormKey.nextProgramClass, "");
    refetchProgramClass({
      languageCode: LANGUAGE_CODE.EN_US,
      solutionOfferingId: id,
    });
  };

  const renderGenericAccountHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={handleSavedFilter}
        onResetAllClick={handleClearAllSearch}
        isSavedFilterDisabled={disableActionBtn}
        anchorRef={anchorRef}
      />
    );
  };

  useEffect(() => {
    const clientName = getValues().clientName;
    if (isNotEmpty(clientName)) {
      setSelectedClientObject({
        id: getValueOrBlank(getValues().clientId),
        label: getValueOrBlank(getValues().clientName),
      });
    } else {
      setSelectedClientObject(undefined);
    }
  }, [getValues().clientId, getValues().clientName]);

  const onChangeClientAutoComplete = (value: AutoCompleteOption) => {
    setValue(GenericAccountsSearchFormKey.clientName, value.label);
  };

  return (
    <GenericAccountsSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={true}
        title={<AccordionHeaderText label={commonLabels.search} />}
      >
        {renderGenericAccountHeader()}
        <form data-testid="search-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className="first-row">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.genericAccountName}
              >
                <MuiTextField
                  label={getLabel("genericAccountName")}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${GenericAccountsSearchFormKey.genericAccountName}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="first-row">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.purpose}
              >
                <MuiSelect
                  visibleDefaultSelectOption
                  id={`${pageId}-${GenericAccountsSearchFormKey.purpose}`}
                  label={getLabel("purpose")}
                  showSearch
                  options={fetchDropdownValues(
                    genericAccountsSearchSiteCoreKeys.searchGenericAccountSection,
                    genericAccountsSearchSiteCoreKeys.purposeList,
                  )}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className="first-row">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.clientId}
                ref={clientRef}
              >
                <ClientAutoComplete
                  label={getLabel("client")}
                  formId={GenericAccountsSearchFormKey.clientId}
                  control={control}
                  onChangeValue={onChangeClientAutoComplete}
                  selectedValue={selectedClientObject ?? undefined}
                  updateValueAlways
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.nextView}
              >
                <MuiSelect
                  label={getLabel("nextView")}
                  options={setApiDataToDropDownOption(solutionOfferings)}
                  showSearch
                  id={`${pageId}-${GenericAccountsSearchFormKey.nextView}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  onChange={(e) =>
                    handleSolutionOfferingChange(e.target.value as string)
                  }
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.country}
              >
                <MuiSelect
                  label={getLabel("country")}
                  options={setApiDataToDropDownOption(countryListData, true)}
                  value={selectedCountryId}
                  onChange={(e) =>
                    handleCountryChange(e.target.value as string)
                  }
                  id={`${pageId}-${GenericAccountsSearchFormKey.country}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.nextProgramClass}
              >
                <MuiSelect
                  label={getLabel("nextProgramClass")}
                  options={setApiDataToDropDownOption(programClasses)}
                  showSearch
                  id={`${pageId}-${GenericAccountsSearchFormKey.nextProgramClass}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.deliveryOffice}
                ref={deliveryOfficeRef}
              >
                <MuiMultiSelect
                  id={`${pageId}-${GenericAccountsSearchFormKey.deliveryOffice}`}
                  options={setApiDataToDropDownOption(deliveryOfficeListData)}
                  label={getLabel("deliveryOffice")}
                  showSelectAllOption
                  showSearch
                  placeholder={commonLabels.selectPlaceHolder}
                  showChip
                  dialogTitle={commonLabels.selected}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.genericAccountStatus}
              >
                <MuiSelect
                  label={getLabel("genericAccountStatus")}
                  options={fetchDropdownValues(
                    genericAccountsSearchSiteCoreKeys.searchGenericAccountSection,
                    genericAccountsSearchSiteCoreKeys.genericAccountStatusList,
                  )}
                  value=""
                  id={`${pageId}-${GenericAccountsSearchFormKey.genericAccountStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={GenericAccountsSearchFormKey.requestedBy}
              >
                <MuiSelect
                  label={getLabel("requestedBy")}
                  options={mapSelectOptionToFirstIndex(
                    mapDropdownDataWithName(usersList, true),
                    commonLabels.selectPlaceHolder,
                  )}
                  value=""
                  className="select-field"
                  id={`${pageId}-${GenericAccountsSearchFormKey.requestedBy}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  searchPlaceholder={commonLabels.search}
                  showSearch
                  getSubText={(
                    option: OptionsDataType | OptionsDataTypeWithNode,
                  ) => option.email}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container className={"button-container"}>
            <MuiButtonSecondary
              onClick={handleSaveFilter}
              id={`${pageId}-saveFilter`}
              data-testid="save-filter"
              disabled={!isValidForm}
            >
              {commonLabels.saveFilter}
            </MuiButtonSecondary>

            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
              data-testid="search"
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </GenericAccountsSearchFormWrapper>
  );
};
